"use client";
import { ReactNode, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { Button, ButtonVariantsType } from "@/components/ui/button";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

export type Props = {
  href: string;
  text: string;
  html_id?: string;
  location?: string;
  class_name?: string;
  name?: string;
  element_type?: string;
  position?: string;
  button_variant?: ButtonVariantsType;
  visible_text?: string | ReactNode; // If provided, will override the text prop so we can differentiate actual text from the tagular event text
  customOnClick?: (e: React.MouseEvent) => void; // Custom onClick handler
  /** Keep ElementViewed to once-per-mount (no reset when scrolling out/in), e.g. search modal pills. */
  stickyElementView?: boolean;
  "aria-label"?: string;
};

export default function EventLink({
  href,
  text,
  html_id = "",
  location = "",
  class_name = "",
  name = "",
  element_type,
  position = "1",
  visible_text,
  button_variant,
  customOnClick,
  stickyElementView = false,
  "aria-label": ariaLabel,
}: Props) {
  const attributedHref = useMarketplaceAttributionHref(href);
  const elementType = element_type
    ? element_type
    : button_variant
      ? ElementType.Button
      : ElementType.Link; // Default is "LINK"

  const webElement: ElementViewed["webElement"] = {
    elementType,
    htmlId: html_id,
    location,
    text,
    position,
    name: name ? hyphenateForTagular(name) : hyphenateForTagular(text),
  };

  const { elementViewed, elementClicked } = useElementEvents({ webElement });
  const { inView, ref } = useInView(
    stickyElementView ? DEFAULT_USE_IN_VIEW_OPTIONS : undefined,
  );
  const viewedRef = useRef(false);

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }

    // Reset when element goes out of view (skip for sticky modal items)
    if (!stickyElementView && !inView) {
      viewedRef.current = false;
    }
  }, [elementViewed, inView, stickyElementView]);

  const handleClick = (e: React.MouseEvent) => {
    // Always track analytics regardless of customOnClick
    elementClicked({ outboundUrl: attributedHref });

    // Execute custom handler if provided
    if (customOnClick) {
      customOnClick(e);
    }
  };

  if (button_variant) {
    return (
      <Button
        variant={button_variant}
        asChild
        className={class_name}
        ref={ref}
        onClick={handleClick}
      >
        <a href={attributedHref} aria-label={ariaLabel}>
          {visible_text ?? text}
        </a>
      </Button>
    );
  }

  return (
    <a
      className={class_name}
      href={attributedHref}
      ref={ref}
      onClick={handleClick}
      aria-label={ariaLabel}
    >
      {visible_text ?? text}
    </a>
  );
}
